// src/components/layout/Sidebar/index.jsx
import React, { useState } from "react";
import { Menu, Avatar, Dropdown, Badge } from "antd";
import { Layout } from "antd";
import {
  EditOutlined,
  LockOutlined,
  SettingOutlined,
  LogoutOutlined,
  EllipsisOutlined,
  BellOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import { ArrowIcon } from "../../../assets/svgs/icons";
import logoText from "@/assets/images/unzippd-wordmark-white-v7lIpH0R.png";
import logoIcon from "@/assets/images/unzippd-logo.png";
import { CURRENT_YEAR, SOCIAL_LINKS } from "@/constants/landing";
import { ADMIN_MENU_SECTIONS } from "@/constants/sidebar.jsx";
import { ChangePasswordModal } from "../../Modals/ChangePasswordModal";
import { EditProfileModal } from "../../Modals/EditProfileModal";
import { LogoutModal } from "../../Modals/LogoutModal";
import { SettingsModal } from "../../Modals/SettingsModal";
import {
  selectCurrentRole,
  selectCurrentUser,
} from "../../../store/auth/authSlice";
import { handleLogout } from "../../../utils/auth";

import { useSelector } from "react-redux";
import { useNotifications } from "../../../hooks/useNotifications";
import { useEnquiriesCount } from "../../../hooks/useEnquiriesCount";

const { Sider } = Layout;

const Sidebar = ({
  notificationsVisible,
  isMobile,
  collapsed,
  setCollapsed,
  setNotificationsVisible,
  handleToggle,
}) => {
  const { count: notificationCount = 0 } = useNotifications() || {};
  const { count: enquiriesCount = 0 } = useEnquiriesCount();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const userRole = useSelector(selectCurrentRole);
  const userData = useSelector(selectCurrentUser);

  const [editProfileVisible, setEditProfileVisible] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [logoutVisible, setLogoutVisible] = useState(false);

  const profile = userData ? userData.data ?? userData : null;
  const nameFromParts = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
    : "";
  const userFullName =
    nameFromParts ||
    profile?.full_name ||
    profile?.name ||
    "Guest User";
  const userImage = profile?.image;

  const openEditProfile = () => {
    handleToggle(false);
    setEditProfileVisible(true);
  };

  const openChangePassword = () => {
    handleToggle(false);
    setChangePasswordVisible(true);
  };

  const openSettings = () => {
    handleToggle(false);
    setSettingsVisible(true);
  };

  const openLogout = () => {
    handleToggle(false);
    setLogoutVisible(true);
  };

  const openNotifications = () => {
    setNotificationsVisible(true);
  };

  const handleMenuNavigate = ({ key }) => {
    handleToggle(false);
    navigate(key);
  };

  const handleConfirmLogout = () => {
    handleLogout(dispatch, navigate);
  };

  const profileMenu = (
    <Menu
      className="w-[235px] py-0 rounded-xl shadow-lg border border-gray-100"
      style={{ background: "white", top: "-0.3rem", left: "3rem" }}
    >
      <Menu.Item
        key="edit"
        icon={<EditOutlined className="text-gray-600" />}
        className="!mx-0 w-full flex items-center gap-2 px-4 py-3 h-auto min-h-0 hover:bg-gray-50 !border-b-0 [&_.ant-menu-title-content]:flex-1"
        onClick={openEditProfile}
      >
        <span className="text-gray-700">Edit Profile</span>
      </Menu.Item>
      <Menu.Item
        onClick={openChangePassword}
        key="password"
        icon={<LockOutlined className="text-gray-600" />}
        className="!w-full !max-w-full !mx-0 flex items-center gap-2 px-4 py-3 h-auto min-h-0 hover:bg-gray-50 !border-b-0 [&_.ant-menu-title-content]:flex-1"
        style={{ width: "100%", marginInline: 0 }}
      >
        <span className="text-gray-700">Change Password</span>
      </Menu.Item>
      <Menu.Item
        onClick={openSettings}
        key="settings"
        icon={<SettingOutlined className="text-gray-600" />}
        className="!w-full !max-w-full !mx-0 flex items-center gap-2 px-4 py-3 h-auto min-h-0 hover:bg-gray-50 !border-b-0 [&_.ant-menu-title-content]:flex-1"
        style={{ width: "100%", marginInline: 0 }}
      >
        <span className="text-gray-700">Settings</span>
      </Menu.Item>
      <Menu.Item
        key="logout"
        icon={<LogoutOutlined className="text-red-500" />}
        className="!w-full !max-w-full !mx-0 flex items-center gap-2 px-4 py-3 h-auto min-h-0 hover:bg-gray-50 text-red-500 !border-b-0 [&_.ant-menu-title-content]:flex-1"
        style={{ width: "100%", marginInline: 0 }}
        onClick={openLogout}
      >
        Logout
      </Menu.Item>
    </Menu>
  );

  return (
    <Sider
      trigger={null}
      collapsible
      collapsedWidth={0}
      width={250}
      collapsed={collapsed}
      onCollapse={setCollapsed}
      className="app-sidebar bg-white border-r border-gray-200 relative h-[100vh] overflow-y-auto"
      breakpoint="md"
    >
      <div className="h-16 flex items-center justify-center border-b border-gray-200 my-3 relative">
        <h1
          className={`text-xl font-bold text-primary transition-opacity duration-200 ${
            collapsed ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="flex items-center gap-3 px-3 py-3">
            <img
              src={logoIcon}
              className="w-8 h-8 brightness-0"
              alt="Unzippd logo"
            />
            <img
              src={logoText}
              className="w-[8rem] brightness-0"
              alt="Unzippd word mark"
            />
          </div>
        </h1>
      </div>
      <div className="px-1.5 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between bg-secondary rounded-2xl px-2">
          <div className="flex items-center gap-3">
            <Avatar
              size="large"
              src={userImage || undefined}
              icon={!userImage ? <UserOutlined className="text-white" /> : null}
              className={`rounded-full ${
                userImage ? "bg-slate-100" : "bg-black"
              }`}
            />
            <div>
              <h3 className="text-sm font-bold text-black mb-0">
                {userFullName}
              </h3>
              <p className="text-xs text-gray-500 mt-0 capitalize">
                {userRole}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Dropdown
              dropdownRender={() => profileMenu}
              trigger={["click"]}
              placement="bottomRight"
              overlayStyle={{ marginTop: 12 }}
            >
              <EllipsisOutlined className="text-lg text-black cursor-pointer" />
            </Dropdown>
            <button
              onClick={openNotifications}
              className="bg-white rounded-full border-0 h-8 w-8 flex justify-center items-center p-0"
            >
              <Badge count={notificationCount} size="small">
                <BellOutlined className="text-gray-600 text-lg" />
              </Badge>
            </button>
          </div>
        </div>
      </div>
      <div className="bg-secondary mt-0 px-2 py-2">
        {ADMIN_MENU_SECTIONS.map((section) => (
          <ul
            key={section.key}
            className="list-none m-0 px-0 py-1 border-0 border-b border-solid border-gray-200 last:border-b-0"
          >
            {section.items.map((item) => {
              const isActive = location.pathname === item.key;
              const isEnquiries = item.key === "/admin/enquiries";

              return (
                <li key={item.key} className="my-1">
                  <button
                    type="button"
                    onClick={() => handleMenuNavigate({ key: item.key })}
                    className={`border-0 w-full bg-transparent text-left px-3 py-2 rounded-xl flex items-center justify-between cursor-pointer ${
                      isActive
                        ? "bg-white text-primary"
                        : "text-gray-700 hover:bg-white/70"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {item.icon}
                      <span className="ml-1">{item.label}</span>
                    </span>
                    {isEnquiries ? (
                      <Badge
                        count={enquiriesCount}
                        size="small"
                        overflowCount={99}
                        style={{ backgroundColor: "#ef4444" }}
                      >
                        <span className="inline-flex">
                          <ArrowIcon />
                        </span>
                      </Badge>
                    ) : (
                      <ArrowIcon />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        ))}
      </div>
      <div
        className="sidebar-footer shrink-0"
        style={{ display: collapsed ? "none" : "" }}
      >
        <div className="flex items-center gap-4">
          <div className="social-links">
            {SOCIAL_LINKS.map(({ href, icon: Icon, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer nofollow"
                aria-label={label}
                title={label}
                className="text-gray-600 hover:text-gray-800"
              >
                <Icon className="w-5 h-5 rounded-[6px]" />
              </a>
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          © {CURRENT_YEAR} Unzippd. All rights reserved.
        </p>
      </div>
      <EditProfileModal
        visible={editProfileVisible}
        onClose={() => setEditProfileVisible(false)}
      />
      <ChangePasswordModal
        visible={changePasswordVisible}
        onClose={() => setChangePasswordVisible(false)}
      />
      <SettingsModal
        visible={settingsVisible}
        onClose={() => setSettingsVisible(false)}
      />
      <LogoutModal
        visible={logoutVisible}
        onClose={() => setLogoutVisible(false)}
        onLogout={handleConfirmLogout}
      />
    </Sider>
  );
};

export default Sidebar;
